/* ============================================================
   HYDONE — Custom Theme JavaScript
   ============================================================ */

(function () {
  'use strict';

  /* ── Utility helpers ─────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && window.moneyFormat) {
      return Shopify.formatMoney(cents, window.moneyFormat);
    }
    const format = window.moneyFormat || '€{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDec = Math.round(cents / 100);
    const amountComma = (cents / 100).toFixed(2).replace('.', ',');

    if (format.includes('{{amount}}')) {
      return format.replace('{{amount}}', amount);
    } else if (format.includes('{{amount_no_decimals}}')) {
      return format.replace('{{amount_no_decimals}}', amountNoDec);
    } else if (format.includes('{{amount_with_comma_separator}}')) {
      return format.replace('{{amount_with_comma_separator}}', amountComma);
    } else if (format.includes('{{amount_no_decimals_with_comma_separator}}')) {
      return format.replace('{{amount_no_decimals_with_comma_separator}}', amountNoDec);
    }
    const symbol = window.currencySymbol || '€';
    return symbol + amount;
  }

  function getShopifyImageUrl(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      url.searchParams.set('format', 'webp');
      return url.pathname + url.search;
    } catch (e) {
      return src;
    }
  }

  function getShopifySrcset(src) {
    if (!src) return '';
    return `${getShopifyImageUrl(src, 360)} 360w, ${getShopifyImageUrl(src, 540)} 540w, ${getShopifyImageUrl(src, 720)} 720w, ${getShopifyImageUrl(src, 900)} 900w`;
  }

  /* ── Sticky Header ────────────────────────────────────── */
  function initStickyHeader() {
    const wrap = qs('#HyHeaderWrap') || qs('.hy-header');
    if (!wrap) return;

    // Keep --hy-header-total in sync with actual wrapper height
    const updateHeight = () => {
      document.documentElement.style.setProperty('--hy-header-total', wrap.offsetHeight + 'px');
    };
    updateHeight();
    if (window.ResizeObserver) {
      new ResizeObserver(updateHeight).observe(wrap);
    }

    const update = () => {
      wrap.classList.toggle('is-scrolled', window.scrollY > 20);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── Mobile Nav ───────────────────────────────────────── */
  function initMobileNav() {
    const burger = qs('#HyBurger');
    const drawer = qs('#HyMobileNav');
    const overlay = qs('#HyNavOverlay');
    const closeBtn = qs('#HyMobileNavClose');
    if (!burger || !drawer) return;

    function openNav() {
      drawer.classList.add('is-open');
      overlay && overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      burger.setAttribute('aria-expanded', 'true');
    }
    function closeNav() {
      drawer.classList.remove('is-open');
      overlay && overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      burger.setAttribute('aria-expanded', 'false');
    }

    on(burger, 'click', openNav);
    on(closeBtn, 'click', closeNav);
    on(overlay, 'click', closeNav);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeNav(); });
  }

  /* ── Scroll Reveal (IntersectionObserver) ─────────────── */
  function initScrollReveal() {
    if (!window.IntersectionObserver) {
      qsa('.hy-reveal').forEach(el => el.classList.add('is-revealed'));
      return;
    }
    const els = qsa('.hy-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px 100px 0px' });
    els.forEach(el => observer.observe(el));

    // Instantly reveal anything currently in or near the viewport on page load
    requestAnimationFrame(() => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      els.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= vh + 100) {
          el.classList.add('is-revealed');
          observer.unobserve(el);
        }
      });
    });
  }

  /* ── Count-up Animation ───────────────────────────────── */
  function initCountUp() {
    if (!window.IntersectionObserver) return;
    const els = qsa('[data-count-up]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseFloat(el.dataset.countUp);
        const suffix = el.dataset.suffix || '';
        const prefix = el.dataset.prefix || '';
        const duration = 1600;
        const decimals = (target % 1 !== 0) ? 1 : 0;
        let start = null;
        function step(ts) {
          if (!start) start = ts;
          const progress = Math.min((ts - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = eased * target;
          el.textContent = prefix + current.toFixed(decimals) + suffix;
          if (progress < 1) requestAnimationFrame(step);
          else el.textContent = prefix + target.toFixed(decimals) + suffix;
        }
        requestAnimationFrame(step);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });
    els.forEach(el => observer.observe(el));
  }

  /* ── Product Gallery ─────────────────────────────────── */
  function initGallery() {
    qsa('.hy-gallery').forEach(gallery => {
      const mainImg = qs('.hy-gallery__main-img', gallery);
      const thumbsEl = qs('.hy-gallery__thumbs', gallery);
      const navPrev = qs('.hy-gallery__nav--prev', gallery);
      const navNext = qs('.hy-gallery__nav--next', gallery);

      function getThumbs() { return qsa('.hy-gallery__thumb', gallery); }

      function activate(thumb, triggerSync = true) {
        if (!thumb) return;
        getThumbs().forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        if (false) {
          // model-viewer removed
        } else if (mainImg) {
          mainImg.src = src ? getShopifyImageUrl(src, 600) : '';
          mainImg.srcset = srcset || (src ? getShopifySrcset(src) : '');
          mainImg.sizes = '(max-width: 767px) 90vw, (max-width: 1200px) 45vw, 600px';
        }
        if (thumbsEl) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

        if (triggerSync) {
          gallery.dispatchEvent(new CustomEvent('hy:gallery-change', {
            bubbles: true,
            detail: { thumb }
          }));
        }
      }

      function navigate(dir) {
        const thumbs = getThumbs();
        if (!thumbs.length) return;
        let current = thumbs.findIndex(t => t.classList.contains('is-active'));
        if (current === -1) {
          if (mainImg) {
            const currentSrc = mainImg.getAttribute('src') || '';
            const found = thumbs.findIndex(t => {
              const fname = t.dataset.src ? t.dataset.src.split('?')[0].split('/').pop() : '';
              return fname && currentSrc.includes(fname);
            });
            current = found !== -1 ? found : 0;
          } else {
            current = 0;
          }
        }
        const next = ((current + dir) % thumbs.length + thumbs.length) % thumbs.length;
        thumbs[next].classList.remove('hy-gallery__thumb--extra');
        if (!qsa('.hy-gallery__thumb--extra', gallery).length) {
          const btn = qs('[data-gallery-more]', gallery);
          if (btn) btn.style.display = 'none';
        }
        activate(thumbs[next], true);
      }

      getThumbs().forEach(thumb => on(thumb, 'click', () => activate(thumb, true)));
      on(navPrev, 'click', () => navigate(-1));
      on(navNext, 'click', () => navigate(1));

      // Sync thumbs container height to main image height so overflow works
      const mainEl = qs('.hy-gallery__main', gallery);
      if (thumbsEl && mainEl) {
        function syncHeight() {
          thumbsEl.style.maxHeight = mainEl.offsetHeight + 'px';
        }
        syncHeight();
        if (window.ResizeObserver) {
          new ResizeObserver(syncHeight).observe(mainEl);
        }
      }

      // Drag-to-scroll: only when dragging a thumb
      if (thumbsEl) {
        let isDragging = false, startY = 0, startScrollTop = 0, dragged = false;

        on(thumbsEl, 'mousedown', e => {
          if (!e.target.closest('.hy-gallery__thumb')) return;
          isDragging = true;
          dragged = false;
          startY = e.clientY;
          startScrollTop = thumbsEl.scrollTop;
          thumbsEl.style.cursor = 'grabbing';
          thumbsEl.style.userSelect = 'none';
          e.preventDefault();
        });

        on(document, 'mousemove', e => {
          if (!isDragging) return;
          const dy = e.clientY - startY;
          if (Math.abs(dy) > 4) dragged = true;
          thumbsEl.scrollTop = startScrollTop - dy;
        });

        on(document, 'mouseup', () => {
          if (!isDragging) return;
          isDragging = false;
          thumbsEl.style.cursor = '';
          thumbsEl.style.userSelect = '';
        });

        // Suppress click on the dragged thumb
        on(thumbsEl, 'click', e => {
          if (dragged) { e.stopPropagation(); dragged = false; }
        }, true);
      }

      const viewMoreBtn = qs('[data-gallery-more]', gallery);
      if (viewMoreBtn) {
        on(viewMoreBtn, 'click', () => {
          qsa('.hy-gallery__thumb--extra', gallery).forEach(t => t.classList.remove('hy-gallery__thumb--extra'));
          viewMoreBtn.style.display = 'none';
        });
      }
    });
  }

  /* ── Variant Selector ────────────────────────────────── */
  function initVariantSelector() {
    qsa('[data-product-form]').forEach(form => {
      const productJSON = qs('[data-product-json]', form);
      if (!productJSON) return;

      let productData;
      try { productData = JSON.parse(productJSON.textContent); } catch (e) { return; }

      const variantInput = qs('[name="id"]', form);
      const buyInfo = form.closest('.hy-buy__info') || form;
      const buySection = form.closest('.hy-buy__layout, .hy-buy, .hy-section') || document;
      const gallery = qs('.hy-gallery', buySection);
      const priceEl = qs('[data-price-current]', buyInfo);
      const comparePriceEl = qs('[data-price-compare]', buyInfo);
      const atcBtn = qs('.hy-btn--atc', form) || qs('.hy-btn--atc-mockup', form) || qs('#HyMainATC');
      const atcTextEl = qs('[data-atc-text]', atcBtn || form);
      const buyNowBtn = qs('#HyBuyNow, [data-buy-now]', form) || qs('#HyBuyNow');
      const buyNowTextEl = qs('[data-buy-now-text]', buyNowBtn || form);
      const skuEl = qs('[data-sku-display]', buyInfo);
      const stickyBtn = qs('#HyStickyBtn');
      const stickyPrice = qs('#HyStickyPrice');
      const stickyCompare = qs('#HyStickyCompare');

      function getSelectedOptions() {
        const groups = qsa('.hy-color-swatches, .hy-capacity-cards, .hy-option-pills', form);
        return groups.map(group => {
          const active = qs('.is-active', group);
          return active ? active.dataset.value : null;
        });
      }

      function findVariant(options) {
        return productData.variants.find(v =>
          v.options.every((opt, i) => opt === options[i])
        ) || null;
      }

      function updateVariant(skipGalleryUpdate = false) {
        const options = getSelectedOptions();
        const variant = findVariant(options);

        // Update label values
        const groups = qsa('.hy-color-swatches, .hy-capacity-cards, .hy-option-pills', form);
        groups.forEach((group, idx) => {
          const lbl = qs(`[data-opt-val="${idx}"]`, buyInfo);
          if (lbl && options[idx]) lbl.textContent = options[idx];
        });

        if (!variant) return;

        // Persist selection in URL query param and sessionStorage ONLY on product pages
        try {
          const isProductPage = document.body.classList.contains('template-product') || window.location.pathname.includes('/products/');
          if (isProductPage) {
            const url = new URL(window.location.href);
            if (url.searchParams.get('variant') !== String(variant.id)) {
              url.searchParams.set('variant', variant.id);
              window.history.replaceState({ variantId: variant.id }, '', url.toString());
            }
            const storageKey = 'hy_selected_variant_' + (productData.id || 'default');
            sessionStorage.setItem(storageKey, String(variant.id));
          }
        } catch (e) { /* ignore */ }

        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));

        // Price
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (comparePriceEl) {
          if (variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price);
            comparePriceEl.hidden = false;
          } else {
            comparePriceEl.hidden = true;
          }
        }

        // SKU display
        if (skuEl && variant.sku) {
          skuEl.textContent = `SKU: ${variant.sku}`;
        }

        // Sticky bar price & compare price
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        if (stickyCompare) {
          if (variant.compare_at_price > variant.price) {
            stickyCompare.textContent = formatMoney(variant.compare_at_price);
            stickyCompare.hidden = false;
          } else {
            stickyCompare.hidden = true;
          }
        }

        // ATC button
        if (atcBtn) {
          const available = variant.available;
          atcBtn.disabled = !available;
          atcBtn.setAttribute('aria-disabled', String(!available));
          if (atcTextEl) {
            atcTextEl.textContent = available ? 'Add to Cart' : 'Sold Out';
          } else {
            const span = qs('span', atcBtn);
            if (span) span.textContent = available ? 'Add to Cart' : 'Sold Out';
          }
        }

        // Buy Now button
        if (buyNowBtn) {
          const available = variant.available;
          buyNowBtn.disabled = !available;
          buyNowBtn.setAttribute('aria-disabled', String(!available));
          if (buyNowTextEl) {
            buyNowTextEl.textContent = available ? 'Buy Now' : 'Sold Out';
          } else {
            const span = qs('span', buyNowBtn);
            if (span) span.textContent = available ? 'Buy Now' : 'Sold Out';
          }
        }

        const stickyATC = qs('#HyStickyATCBtn, #HyStickyBtn');
        if (stickyATC) {
          stickyATC.disabled = !variant.available;
          stickyATC.setAttribute('aria-disabled', String(!variant.available));
          const span = qs('span', stickyATC);
          if (span) span.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }

        const stickyBuy = qs('#HyStickyBuyNow');
        if (stickyBuy) {
          if (stickyBuy.tagName === 'BUTTON') {
            stickyBuy.disabled = !variant.available;
            stickyBuy.setAttribute('aria-disabled', String(!variant.available));
            const span = qs('span', stickyBuy);
            if (span) span.textContent = variant.available ? 'Buy Now' : 'Sold Out';
          } else {
            const span = qs('span', stickyBuy);
            if (span) span.textContent = 'Shop Now';
          }
        }

        // Quantity rules update
        const qtyInput = qs('[name="quantity"]', form);
        if (qtyInput && productData.quantity_rules && productData.quantity_rules[variant.id]) {
          const rule = productData.quantity_rules[variant.id];
          const minVal = rule.min || 1;
          const stepVal = rule.increment || 1;
          const maxVal = (rule.max !== null && rule.max !== undefined) ? rule.max : 50;

          qtyInput.min = minVal;
          qtyInput.dataset.min = minVal;
          qtyInput.step = stepVal;
          qtyInput.dataset.step = stepVal;
          qtyInput.max = maxVal;
          qtyInput.dataset.max = maxVal;

          let cur = parseInt(qtyInput.value) || minVal;
          if (cur < minVal) cur = minVal;
          if (cur > maxVal) cur = maxVal;
          qtyInput.value = cur;
        }

        // Gallery: update main image if variant has image and we didn't initiate this from gallery click
        if (!skipGalleryUpdate && variant.featured_image) {
          const gal = gallery || form.closest('.hy-buy__layout, .hy-section') || document;
          const galleryImg = qs('.hy-gallery__main-img', gal);
          if (galleryImg) {
            galleryImg.src = getShopifyImageUrl(variant.featured_image.src, 600);
            galleryImg.srcset = getShopifySrcset(variant.featured_image.src);
            galleryImg.sizes = '(max-width: 767px) 90vw, (max-width: 1200px) 45vw, 600px';
            // Activate matching thumb
            const thumbs = qsa('.hy-gallery__thumb', gal);
            let hasMatch = false;
            const featFileName = variant.featured_image.src ? variant.featured_image.src.split('?')[0].split('/').pop() : '';
            thumbs.forEach(t => {
              const matchesId = variant.featured_image.id && t.dataset.mediaId === String(variant.featured_image.id);
              const thumbFileName = t.dataset.src ? t.dataset.src.split('?')[0].split('/').pop() : '';
              const matchesFile = featFileName && thumbFileName && (featFileName === thumbFileName || thumbFileName.includes(featFileName) || featFileName.includes(thumbFileName));
              const isMatch = Boolean(matchesId || matchesFile);
              t.classList.toggle('is-active', isMatch);
              if (isMatch) hasMatch = true;
            });
            if (!hasMatch && thumbs.length) {
              thumbs[0].classList.add('is-active');
            }
          }
        }
      }

      function selectVariantById(targetVariantId, skipGallery = false) {
        if (!targetVariantId || !productData || !productData.variants) return false;
        const targetVariant = productData.variants.find(v => String(v.id) === String(targetVariantId));
        if (!targetVariant) return false;

        const groups = qsa('.hy-color-swatches, .hy-capacity-cards, .hy-option-pills', form);
        groups.forEach((group, idx) => {
          const targetVal = targetVariant.options[idx];
          if (!targetVal) return;
          const items = qsa('.hy-color-swatch, .hy-capacity-card, .hy-option-pill', group);
          items.forEach(item => {
            const isMatch = item.dataset.value && item.dataset.value.trim().toLowerCase() === targetVal.trim().toLowerCase();
            item.classList.toggle('is-active', isMatch);
            item.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
          });
        });

        updateVariant(skipGallery);
        return true;
      }

      // Match thumb to variant and sync option buttons
      function syncVariantFromThumb(thumb) {
        if (!thumb || thumb.dataset.type === 'model' || !productData || !productData.variants) return;

        const mediaId = thumb.dataset.mediaId;
        const variantId = thumb.dataset.variantId;
        const variantIds = thumb.dataset.variantIds ? thumb.dataset.variantIds.split(',') : [];
        const thumbSrc = thumb.dataset.src || '';
        const thumbFile = thumbSrc.split('?')[0].split('/').pop().toLowerCase();
        const imgEl = qs('img', thumb);
        const thumbAlt = (thumb.dataset.alt || (imgEl ? imgEl.getAttribute('alt') : '') || '').toLowerCase();

        // 1. Find candidates
        let candidates = [];

        // By variant ID
        if (variantId) {
          const v = productData.variants.find(x => String(x.id) === String(variantId));
          if (v) candidates.push(v);
        }

        // By variant IDs list
        if (!candidates.length && variantIds.length) {
          candidates = productData.variants.filter(x => variantIds.includes(String(x.id)));
        }

        // By mediaId / featured_image.id
        if (!candidates.length && mediaId) {
          candidates = productData.variants.filter(x =>
            (x.featured_image && String(x.featured_image.id) === String(mediaId)) ||
            (x.featured_media && String(x.featured_media.id) === String(mediaId))
          );
        }

        // By featured_image filename match
        if (!candidates.length && thumbFile) {
          candidates = productData.variants.filter(x => {
            const vSrc = x.featured_image?.src || '';
            const vFile = vSrc.split('?')[0].split('/').pop().toLowerCase();
            return vFile && (vFile === thumbFile || thumbFile.includes(vFile) || vFile.includes(thumbFile));
          });
        }

        // By option values in alt text or filename (e.g., color name like "black", "purple", "pink", "green")
        if (!candidates.length && (thumbAlt || thumbFile)) {
          productData.variants.forEach(v => {
            const matchesOpt = v.options.some(opt => {
              if (!opt) return false;
              const optClean = opt.trim().toLowerCase();
              return (thumbAlt && thumbAlt.includes(optClean)) || (thumbFile && thumbFile.includes(optClean));
            });
            if (matchesOpt && !candidates.includes(v)) {
              candidates.push(v);
            }
          });
        }

        if (!candidates.length) return;

        // 2. If multiple candidates, pick the one matching most current selected options
        const currentSelected = getSelectedOptions();
        let bestVariant = candidates[0];
        let bestScore = -1;

        candidates.forEach(v => {
          let score = 0;
          v.options.forEach((opt, idx) => {
            if (currentSelected[idx] && opt.toLowerCase() === currentSelected[idx].toLowerCase()) {
              score++;
            }
          });
          if (score > bestScore) {
            bestScore = score;
            bestVariant = v;
          }
        });

        if (!bestVariant) return;

        // 3. Update the UI option buttons (swatches, capacity, pills) to reflect bestVariant.options
        const groups = qsa('.hy-color-swatches, .hy-capacity-cards, .hy-option-pills', form);
        groups.forEach((group, idx) => {
          const targetVal = bestVariant.options[idx];
          if (!targetVal) return;
          const items = qsa('.hy-color-swatch, .hy-capacity-card, .hy-option-pill', group);
          items.forEach(item => {
            const isMatch = item.dataset.value && item.dataset.value.trim().toLowerCase() === targetVal.trim().toLowerCase();
            item.classList.toggle('is-active', isMatch);
            item.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
          });
        });

        // 4. Update variant data with skipGalleryUpdate = true
        updateVariant(true);
      }

      // Handle item clicks (swatches, capacity cards, pills)
      qsa('.hy-color-swatches, .hy-capacity-cards, .hy-option-pills', form).forEach(group => {
        on(group, 'click', e => {
          const item = e.target.closest('.hy-color-swatch, .hy-capacity-card, .hy-option-pill');
          if (!item || item.getAttribute('aria-disabled') === 'true') return;
          qsa('.hy-color-swatch, .hy-capacity-card, .hy-option-pill', group).forEach(p => {
            p.classList.remove('is-active');
            p.setAttribute('aria-pressed', 'false');
          });
          item.classList.add('is-active');
          item.setAttribute('aria-pressed', 'true');
          updateVariant(false);
        });
      });

      // Listen for gallery change events to sync variant
      const containerToListen = gallery || buySection || document;
      on(containerToListen, 'hy:gallery-change', e => {
        if (e.detail && e.detail.thumb) {
          syncVariantFromThumb(e.detail.thumb);
        }
      });

      // Also directly attach click listener to any thumbs in gallery
      if (gallery) {
        qsa('.hy-gallery__thumb', gallery).forEach(thumb => {
          on(thumb, 'click', () => {
            syncVariantFromThumb(thumb);
          });
        });
      }

      // Initial variant restoration from URL query param or sessionStorage (Product pages only)
      function restoreInitialVariant() {
        const isProductPage = document.body.classList.contains('template-product') || window.location.pathname.includes('/products/');
        if (!isProductPage) {
          updateVariant(false);
          return;
        }

        let targetId = null;
        try {
          const urlParams = new URLSearchParams(window.location.search);
          targetId = urlParams.get('variant');
          if (!targetId && productData.id) {
            targetId = sessionStorage.getItem('hy_selected_variant_' + productData.id);
          }
        } catch (e) { }

        if (targetId && selectVariantById(targetId, false)) {
          return;
        }
        updateVariant(false);
      }

      restoreInitialVariant();

      // Listen for browser Back/Forward (popstate) and bfcache page restores (pageshow)
      window.addEventListener('popstate', restoreInitialVariant);
      window.addEventListener('pageshow', restoreInitialVariant);
    });
  }

  /* ── Quantity Selector ───────────────────────────────── */
  function initQtySelector() {
    qsa('.hy-qty, .hy-qty-pill').forEach(qty => {
      const input = qs('.hy-qty__input, .hy-qty-pill__input', qty);
      if (!input) return;

      function clampValue(val) {
        const min = parseInt(input.min) || parseInt(input.dataset.min) || 1;
        const max = parseInt(input.max) || parseInt(input.dataset.max) || 50;

        let num = isNaN(val) ? min : val;
        if (num < min) num = min;
        if (num > max) num = max;
        return num;
      }

      on(qty, 'click', e => {
        const btn = e.target.closest('.hy-qty__btn, .hy-qty-pill__btn');
        if (!btn) return;
        const step = parseInt(input.step) || parseInt(input.dataset.step) || 1;
        const delta = (btn.dataset.action === 'plus' ? 1 : -1) * step;
        const current = parseInt(input.value) || 1;
        input.value = clampValue(current + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      on(input, 'change', () => {
        input.value = clampValue(parseInt(input.value));
      });

      on(input, 'input', () => {
        const max = parseInt(input.max) || parseInt(input.dataset.max) || 50;
        if (parseInt(input.value) > max) {
          input.value = max;
        }
      });
    });
  }

  /* ── AJAX Add to Cart & Buy Now ──────────────────────── */
  function initAjaxCart() {
    function resetActionButtons() {
      qsa('.hy-btn--loading').forEach(b => b.classList.remove('hy-btn--loading'));
      const stickyATC = qs('#HyStickyATCBtn, #HyStickyBtn');
      const stickyBuy = qs('#HyStickyBuyNow');
      qsa('[data-product-form]').forEach(form => {
        const atcBtn = qs('.hy-btn--atc', form) || qs('.hy-btn--atc-mockup', form) || qs('#HyMainATC') || qs('[type="submit"]', form);
        const buyNowBtn = qs('#HyBuyNow, [data-buy-now]', form) || qs('#HyBuyNow');
        const variantInput = qs('[name="id"]', form);
        const productJSON = qs('[data-product-json]', form);
        let isAvailable = true;
        if (productJSON && variantInput) {
          try {
            const pData = JSON.parse(productJSON.textContent);
            const v = pData.variants.find(x => String(x.id) === String(variantInput.value));
            if (v) isAvailable = v.available;
          } catch (e) { }
        }
        if (atcBtn) {
          atcBtn.classList.remove('hy-btn--loading');
          atcBtn.disabled = !isAvailable;
          atcBtn.setAttribute('aria-disabled', String(!isAvailable));
        }
        if (buyNowBtn) {
          buyNowBtn.classList.remove('hy-btn--loading');
          buyNowBtn.disabled = !isAvailable;
          buyNowBtn.setAttribute('aria-disabled', String(!isAvailable));
        }
        if (stickyATC) {
          stickyATC.classList.remove('hy-btn--loading');
          stickyATC.disabled = !isAvailable;
          stickyATC.setAttribute('aria-disabled', String(!isAvailable));
        }
        if (stickyBuy) {
          stickyBuy.classList.remove('hy-btn--loading');
          stickyBuy.disabled = !isAvailable;
          stickyBuy.setAttribute('aria-disabled', String(!isAvailable));
        }
      });
    }

    // Reset button loading state & sync cart count on back/forward cache restore and page transitions
    window.addEventListener('pageshow', () => {
      resetActionButtons();
      refreshCartCount();
    });
    window.addEventListener('pagehide', resetActionButtons);

    // Handle product forms (Add to Cart -> redirects to /cart)
    qsa('[data-product-form]').forEach(form => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const btn = qs('.hy-btn--atc', form) || qs('.hy-btn--atc-mockup', form) || qs('#HyMainATC') || qs('[type="submit"]', form);
        const buyNowBtn = qs('#HyBuyNow, [data-buy-now]', form) || qs('#HyBuyNow');
        const stickyATC = qs('#HyStickyATCBtn, #HyStickyBtn');
        const stickyBuy = qs('#HyStickyBuyNow');
        if (btn?.disabled) return;

        const variantId = qs('[name="id"]', form)?.value;
        const qtyInput = qs('[name="quantity"]', form);
        const qty = parseInt(qtyInput?.value) || 1;
        if (!variantId) return;

        // Dynamic max quantity check against existing cart items
        const maxRule = parseInt(qtyInput?.max) || parseInt(qtyInput?.dataset?.max) || 50;

        try {
          const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
          const cartData = await cartRes.json();
          const currentItem = cartData.items.find(item => String(item.variant_id) === String(variantId) || String(item.id) === String(variantId));
          const existingQty = currentItem ? currentItem.quantity : 0;
          if (existingQty + qty > maxRule) {
            const allowedToAdd = Math.max(0, maxRule - existingQty);
            if (allowedToAdd <= 0) {
              showToast(`Maximum quantity of ${maxRule} for this item is already in your cart.`);
            } else {
              showToast(`You can only add ${allowedToAdd} more (limit: ${maxRule}).`);
            }
            return;
          }
        } catch (e) {
          /* If cart fetch fails, continue to server validation */
        }

        // Loading state
        if (btn) {
          btn.classList.add('hy-btn--loading');
          btn.disabled = true;
        }
        if (stickyATC) {
          stickyATC.classList.add('hy-btn--loading');
          stickyATC.disabled = true;
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
        }
        if (stickyBuy) {
          stickyBuy.disabled = true;
        }

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty })
          });
          const data = await res.json();

          if (!res.ok) {
            showToast(data.description || 'Unable to add to cart.');
            resetActionButtons();
            return;
          }

          // Fire Meta Pixel AddToCart event
          if (typeof window.fbq === 'function') {
            try {
              window.fbq('track', 'AddToCart', {
                content_name: data.title || data.product_title || 'Hydone Bottle',
                content_ids: [String(data.id || variantId)],
                content_type: 'product',
                value: data.price ? Number((data.price / 100).toFixed(2)) : undefined,
                currency: 'EUR'
              });
            } catch (_) { }
          }

          // Immediately update cart badge in DOM so bfcache snapshot captures the new count
          await refreshCartCount();

          // User requested: Add to Cart button takes user directly to cart page
          const cartUrl = window.routes?.cart_url || (window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart');
          setTimeout(resetActionButtons, 1000);
          setTimeout(() => {
            window.location.href = cartUrl;
          }, 180);
        } catch (err) {
          showToast('Something went wrong. Please try again.');
          resetActionButtons();
        }
      });
    });

    // Handle Buy Now buttons (Direct checkout)
    qsa('#HyBuyNow, [data-buy-now]').forEach(buyNowBtn => {
      on(buyNowBtn, 'click', async (e) => {
        e.preventDefault();
        if (buyNowBtn.disabled) return;

        const form = buyNowBtn.closest('form') || qs('[data-product-form]');
        if (!form) return;

        const atcBtn = qs('.hy-btn--atc', form) || qs('.hy-btn--atc-mockup', form) || qs('#HyMainATC');
        const stickyATC = qs('#HyStickyATCBtn, #HyStickyBtn');
        const stickyBuy = qs('#HyStickyBuyNow');
        const variantId = qs('[name="id"]', form)?.value;
        const qtyInput = qs('[name="quantity"]', form);
        const qty = parseInt(qtyInput?.value) || 1;
        if (!variantId) return;

        // Loading state
        buyNowBtn.classList.add('hy-btn--loading');
        buyNowBtn.disabled = true;
        if (stickyBuy) {
          stickyBuy.classList.add('hy-btn--loading');
          stickyBuy.disabled = true;
        }
        if (atcBtn) atcBtn.disabled = true;
        if (stickyATC) stickyATC.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty })
          });
          const data = await res.json();

          if (!res.ok) {
            showToast(data.description || 'Unable to proceed to checkout.');
            resetActionButtons();
            return;
          }

          if (typeof window.fbq === 'function') {
            try {
              window.fbq('track', 'InitiateCheckout', {
                content_name: data.title || data.product_title || 'Hydone Bottle',
                content_ids: [String(data.id || variantId)],
                content_type: 'product',
                value: data.price ? Number((data.price / 100).toFixed(2)) : undefined,
                currency: 'EUR'
              });
            } catch (_) { }
          }

          const checkoutUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}checkout` : '/checkout';
          setTimeout(resetActionButtons, 1000);
          setTimeout(() => {
            window.location.href = checkoutUrl;
          }, 180);
        } catch (err) {
          showToast('Something went wrong. Please try again.');
          resetActionButtons();
        }
      });
    });

    // Sticky ATC button
    const stickyBtn = qs('#HyStickyBtn');
    on(stickyBtn, 'click', () => {
      const mainForm = qs('[data-product-form]');
      if (mainForm) mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js?_t=' + Date.now(), {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) return;
      const cart = await res.json();
      const count = Number(cart.item_count) || 0;
      qsa('.hy-cart-badge').forEach(badge => {
        badge.textContent = count > 0 ? String(count) : '';
        badge.dataset.count = String(count);
        if (count > 0) {
          badge.removeAttribute('hidden');
          badge.style.display = 'flex';
        } else {
          badge.setAttribute('hidden', '');
          badge.style.display = 'none';
        }
      });
      qsa('.hy-header__cart').forEach(link => {
        link.setAttribute('aria-label', `Cart (${count} items)`);
      });
    } catch (e) { /* silent */ }
  }

  /* ── Sticky ATC Bar & Mobile Quick-Buy Trigger ───────── */
  function initStickyATCBar() {
    const bar = qs('#HyStickyATC');
    if (!bar) return;

    function setupObserver() {
      const buySection = qs('#buy, .hy-buy');

      // Populate sticky bar info
      function syncBarInfo() {
        const titleEl = qs('.hy-buy__title');
        const priceEl = qs('[data-price-current]');
        const compareEl = qs('[data-price-compare]');
        const imgEl = qs('.hy-gallery__main-img', document);

        const stickyTitle = qs('#HyStickyTitle', bar);
        const stickyPrice = qs('#HyStickyPrice', bar);
        const stickyCompare = qs('#HyStickyCompare', bar);
        const stickyImg = qs('#HyStickyImg', bar);

        if (stickyTitle && titleEl) stickyTitle.textContent = titleEl.textContent;
        if (stickyPrice && priceEl) stickyPrice.textContent = priceEl.textContent;
        if (stickyCompare) {
          if (compareEl && compareEl.textContent.trim()) {
            stickyCompare.textContent = compareEl.textContent;
            stickyCompare.hidden = false;
          } else {
            stickyCompare.hidden = true;
          }
        }
        if (stickyImg && imgEl && imgEl.src) {
          stickyImg.src = getShopifyImageUrl(imgEl.src, 120);
          stickyImg.alt = imgEl.alt || 'Hydone Bottle';
        }
      }
      syncBarInfo();

      function isBuyBoxActive() {
        const onPageActions = qs('#HyBuyNow') || qs('#HyMainATC') || qs('.hy-buy__actions');
        if (!onPageActions) return false;
        const rect = onPageActions.getBoundingClientRect();

        // Only hide the sticky bar when the on-page CTA buttons are physically visible on the screen
        return rect.bottom > 0 && rect.top < window.innerHeight;
      }

      let lastScrollY = window.scrollY;
      let scrollDirection = 'down';

      function updateBar() {
        const isBuyBox = isBuyBoxActive();

        // Search for payment gateway icons / bottom bar at end of page
        let paymentInView = false;
        const paymentTarget = qs('.hy-footer__payments, .footer__payment, .hy-footer__bottom');
        if (paymentTarget) {
          const payRect = paymentTarget.getBoundingClientRect();
          paymentInView = payRect.top < window.innerHeight && payRect.bottom > 0;
        }

        // Hide when viewing the main on-page action buttons, OR when at payment gateway icons while scrolling downwards.
        const hideForPayment = paymentInView && scrollDirection !== 'up';
        const hide = isBuyBox || hideForPayment;

        bar.classList.toggle('is-hidden', hide);
        bar.setAttribute('aria-hidden', String(hide));
      }

      let ticking = false;
      function onScroll() {
        const currentScrollY = window.scrollY;
        const diff = currentScrollY - lastScrollY;
        if (Math.abs(diff) > 2) {
          scrollDirection = diff > 0 ? 'down' : 'up';
          lastScrollY = currentScrollY;
        }

        if (!ticking) {
          requestAnimationFrame(() => {
            updateBar();
            ticking = false;
          });
          ticking = true;
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      window.addEventListener('orientationchange', onScroll, { passive: true });

      // Mobile touch gestures for instant upward scroll detection
      let touchStartY = 0;
      window.addEventListener('touchstart', (e) => {
        if (e.touches && e.touches[0]) {
          touchStartY = e.touches[0].clientY;
        }
      }, { passive: true });

      window.addEventListener('touchmove', (e) => {
        if (e.touches && e.touches[0]) {
          const currentY = e.touches[0].clientY;
          const touchDiff = currentY - touchStartY;
          if (touchDiff > 4) {
            // Dragging downward on screen = scrolling UPWARDS
            scrollDirection = 'up';
            updateBar();
          } else if (touchDiff < -4) {
            // Dragging upward on screen = scrolling DOWNWARDS
            scrollDirection = 'down';
            updateBar();
          }
          touchStartY = currentY;
        }
      }, { passive: true });

      // Hook click events on sticky bar (both mobile and desktop)
      const stickyBuyNow = qs('#HyStickyBuyNow', bar);
      if (stickyBuyNow) {
        on(stickyBuyNow, 'click', (e) => {
          const isProductPage = window.location.pathname.includes('/products/');
          if (!isProductPage) {
            // Natural link navigation to product details page
            return;
          }
          e.preventDefault();
          const mainBuyNow = qs('#HyBuyNow');
          if (mainBuyNow) {
            mainBuyNow.click();
          } else {
            const form = qs('[data-product-form]');
            if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
      }

      const stickyAtcBtn = qs('#HyStickyATCBtn, #HyStickyBtn', bar);
      if (stickyAtcBtn) {
        on(stickyAtcBtn, 'click', (e) => {
          const isProductPage = window.location.pathname.includes('/products/');
          if (!isProductPage) {
            return;
          }
          e.preventDefault();
          const mainATC = qs('#HyMainATC');
          if (mainATC) {
            mainATC.click();
          } else {
            const mainForm = qs('[data-product-form]');
            if (mainForm) mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
      }

      const stickyInfo = qs('#HyStickyInfoTrigger', bar);
      if (stickyInfo) {
        on(stickyInfo, 'click', (e) => {
          const isProductPage = window.location.pathname.includes('/products/');
          if (!isProductPage) {
            // Natural link navigation to product details page
            return;
          }
          if (buySection) {
            e.preventDefault();
            buySection.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }

      updateBar();
    }

    setupObserver();
  }

  /* ── Mobile Quick-Buy Drawer Controller ──────────────── */
  function initQuickBuyDrawer() {
    const sheet = qs('#HyQuickBuySheet');
    const overlay = qs('#HyQuickBuyOverlay');
    if (!sheet || !overlay) return;

    const closeBtn = qs('#HyQuickBuyClose');
    const productJSON = qs('#HyQuickBuyProductJSON') || qs('[data-product-json]');
    let productData = null;
    if (productJSON) {
      try { productData = JSON.parse(productJSON.textContent); } catch (e) { }
    }

    const priceEl = qs('#HyQuickBuyPrice');
    const comparePriceEl = qs('#HyQuickBuyCompare');
    const colorLabel = qs('#HyQuickBuyColorLabel');
    const imgEl = qs('#HyQuickBuyImg');
    const variantInput = qs('#HyQuickBuyVariantId');
    const qtyInput = qs('#HyQuickBuyQty');
    const buyNowBtn = qs('#HyQuickBuySubmit');
    const atcBtn = qs('#HyQuickBuyATC');

    function openDrawer(action) {
      // Sync active variant from main section if selected
      const activeMainSwatch = qs('.hy-buy .hy-color-swatch.is-active, [data-product-form] .hy-color-swatch.is-active');
      if (activeMainSwatch && activeMainSwatch.dataset.value) {
        const val = activeMainSwatch.dataset.value;
        const qbSwatch = qs(`.hy-quickbuy-swatches .hy-color-swatch[data-value="${CSS.escape(val)}"]`);
        if (qbSwatch && !qbSwatch.classList.contains('is-active')) {
          qbSwatch.click();
        }
      }

      sheet.classList.add('is-open');
      sheet.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (action === 'buy_now' && buyNowBtn) {
        setTimeout(() => buyNowBtn.focus(), 150);
      } else if (action === 'atc' && atcBtn) {
        setTimeout(() => atcBtn.focus(), 150);
      }
    }

    function closeDrawer() {
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    window.HyOpenQuickBuy = openDrawer;
    window.HyCloseQuickBuy = closeDrawer;

    on(closeBtn, 'click', closeDrawer);
    on(overlay, 'click', closeDrawer);
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheet.classList.contains('is-open')) closeDrawer();
    });

    // Touch drag down on handle to close
    let startY = 0;
    const handle = qs('.hy-quickbuy-sheet__handle', sheet);
    if (handle) {
      on(handle, 'touchstart', (e) => {
        startY = e.touches[0].clientY;
      }, { passive: true });
      on(handle, 'touchend', (e) => {
        const diff = e.changedTouches[0].clientY - startY;
        if (diff > 50) closeDrawer();
      }, { passive: true });
    }

    // Color Swatch Selection
    const swatches = qsa('.hy-quickbuy-swatches .hy-color-swatch');
    swatches.forEach(swatch => {
      on(swatch, 'click', () => {
        const val = swatch.dataset.value;
        if (!val || !productData) return;

        swatches.forEach(s => s.classList.toggle('is-active', s === swatch));
        if (colorLabel) colorLabel.textContent = val;

        const variant = productData.variants.find(v => v.options.includes(val) || v.title.toLowerCase().includes(val.toLowerCase()));
        if (variant) {
          if (variantInput) variantInput.value = variant.id;
          if (priceEl) priceEl.textContent = formatMoney(variant.price);
          if (comparePriceEl) {
            if (variant.compare_at_price > variant.price) {
              comparePriceEl.textContent = formatMoney(variant.compare_at_price);
              comparePriceEl.hidden = false;
            } else {
              comparePriceEl.hidden = true;
            }
          }
          if (imgEl && variant.featured_image) {
            imgEl.src = getShopifyImageUrl(variant.featured_image.src, 160);
          }

          const avail = variant.available;
          if (buyNowBtn) {
            buyNowBtn.disabled = !avail;
            buyNowBtn.setAttribute('aria-disabled', String(!avail));
            const txt = qs('[data-quick-buy-text]', buyNowBtn);
            if (txt) txt.textContent = avail ? 'Buy Now' : 'Sold Out';
          }
          if (atcBtn) {
            atcBtn.disabled = !avail;
            atcBtn.setAttribute('aria-disabled', String(!avail));
            const txt = qs('[data-quick-atc-text]', atcBtn);
            if (txt) txt.textContent = avail ? 'Add to Cart' : 'Sold Out';
          }

          // Sync back to main buy form if present
          const mainSwatch = qs(`.hy-color-swatches:not(.hy-quickbuy-swatches) [data-value="${CSS.escape(val)}"]`);
          if (mainSwatch && !mainSwatch.classList.contains('is-active')) {
            mainSwatch.click();
          }
        }
      });
    });

    // Quantity buttons
    const qtyWrap = qs('.hy-quickbuy-qty');
    if (qtyWrap && qtyInput) {
      on(qtyWrap, 'click', (e) => {
        const btn = e.target.closest('.hy-qty-pill__btn');
        if (!btn) return;
        const cur = parseInt(qtyInput.value) || 1;
        const delta = btn.dataset.action === 'plus' ? 1 : -1;
        const next = Math.max(1, Math.min(50, cur + delta));
        qtyInput.value = next;
      });
    }

    // Quick Buy Now Submit -> Redirect directly to /checkout
    if (buyNowBtn) {
      on(buyNowBtn, 'click', async (e) => {
        e.preventDefault();
        if (buyNowBtn.disabled) return;
        const variantId = variantInput?.value;
        const qty = parseInt(qtyInput?.value) || 1;
        if (!variantId) return;

        buyNowBtn.classList.add('hy-btn--loading');
        buyNowBtn.disabled = true;
        if (atcBtn) atcBtn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty })
          });
          const data = await res.json();
          if (!res.ok) {
            showToast(data.description || 'Unable to proceed to checkout.');
            buyNowBtn.classList.remove('hy-btn--loading');
            buyNowBtn.disabled = false;
            if (atcBtn) atcBtn.disabled = false;
            return;
          }
          if (typeof window.fbq === 'function') {
            try {
              window.fbq('track', 'InitiateCheckout', {
                content_name: data.title || data.product_title || 'Hydone Bottle',
                content_ids: [String(data.id || variantId)],
                content_type: 'product',
                value: data.price ? Number((data.price / 100).toFixed(2)) : undefined,
                currency: 'EUR'
              });
            } catch (_) { }
          }
          const checkoutUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}checkout` : '/checkout';
          setTimeout(() => {
            buyNowBtn.classList.remove('hy-btn--loading');
            buyNowBtn.disabled = false;
            if (atcBtn) atcBtn.disabled = false;
          }, 1000);
          setTimeout(() => {
            window.location.href = checkoutUrl;
          }, 180);
        } catch (err) {
          showToast('Something went wrong. Please try again.');
          buyNowBtn.classList.remove('hy-btn--loading');
          buyNowBtn.disabled = false;
          if (atcBtn) atcBtn.disabled = false;
        }
      });
    }

    // Quick Add to Cart Submit -> Redirect directly to /cart
    if (atcBtn) {
      on(atcBtn, 'click', async (e) => {
        e.preventDefault();
        if (atcBtn.disabled) return;
        const variantId = variantInput?.value;
        const qty = parseInt(qtyInput?.value) || 1;
        if (!variantId) return;

        atcBtn.classList.add('hy-btn--loading');
        atcBtn.disabled = true;
        if (buyNowBtn) buyNowBtn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty })
          });
          const data = await res.json();
          if (!res.ok) {
            showToast(data.description || 'Unable to add to cart.');
            atcBtn.classList.remove('hy-btn--loading');
            atcBtn.disabled = false;
            if (buyNowBtn) buyNowBtn.disabled = false;
            return;
          }

          if (typeof window.fbq === 'function') {
            try {
              window.fbq('track', 'AddToCart', {
                content_name: data.title || data.product_title || 'Hydone Bottle',
                content_ids: [String(data.id || variantId)],
                content_type: 'product',
                value: data.price ? Number((data.price / 100).toFixed(2)) : undefined,
                currency: 'EUR'
              });
            } catch (_) { }
          }

          await refreshCartCount();
          const cartUrl = window.routes?.cart_url || (window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart');
          setTimeout(() => {
            atcBtn.classList.remove('hy-btn--loading');
            atcBtn.disabled = false;
            if (buyNowBtn) buyNowBtn.disabled = false;
          }, 1000);
          setTimeout(() => {
            window.location.href = cartUrl;
          }, 180);
        } catch (err) {
          showToast('Something went wrong. Please try again.');
          atcBtn.classList.remove('hy-btn--loading');
          atcBtn.disabled = false;
          if (buyNowBtn) buyNowBtn.disabled = false;
        }
      });
    }

    // Reset loading state and sync cart on pageshow/pagehide
    window.addEventListener('pageshow', () => {
      if (buyNowBtn) {
        buyNowBtn.classList.remove('hy-btn--loading');
        buyNowBtn.disabled = false;
      }
      if (atcBtn) {
        atcBtn.classList.remove('hy-btn--loading');
        atcBtn.disabled = false;
      }
      refreshCartCount();
    });

    // End of initQuickBuyDrawer
  }

  /* ── FAQ Accordion ───────────────────────────────────── */
  function initAccordion() {
    qsa('.hy-accordion').forEach(accordion => {
      on(accordion, 'click', e => {
        const trigger = e.target.closest('.hy-accordion__trigger');
        if (!trigger) return;

        const item = trigger.closest('.hy-accordion__item');
        const body = qs('.hy-accordion__body', item);
        const isOpen = trigger.classList.contains('is-open');

        // Optionally close all others
        qsa('.hy-accordion__trigger.is-open', accordion).forEach(t => {
          if (t !== trigger) {
            t.classList.remove('is-open');
            t.setAttribute('aria-expanded', 'false');
            const b = qs('.hy-accordion__body', t.closest('.hy-accordion__item'));
            if (b) b.classList.remove('is-open');
          }
        });

        trigger.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
        if (body) body.classList.toggle('is-open', !isOpen);
      });
    });
  }

  /* ── Tabs & Spec Tabs ─────────────────────────────────── */
  function initTabs() {
    // Generic tabs
    qsa('.hy-tabs').forEach(tabs => {
      const btns = qsa('.hy-tabs__btn', tabs);
      const panels = qsa('.hy-tabs__panel', tabs);

      on(tabs, 'click', e => {
        const btn = e.target.closest('.hy-tabs__btn');
        if (!btn) return;
        const idx = btns.indexOf(btn);
        btns.forEach((b, i) => {
          b.classList.toggle('is-active', i === idx);
          b.setAttribute('aria-selected', String(i === idx));
        });
        panels.forEach((p, i) => {
          p.classList.toggle('is-active', i === idx);
        });
      });
    });

    // Product Spec Tabs
    qsa('.hy-spec-tabs').forEach(specTabs => {
      const btns = qsa('.hy-spec-tabs__btn', specTabs);
      const panels = qsa('.hy-spec-tabs__panel', specTabs);

      on(specTabs, 'click', e => {
        const btn = e.target.closest('.hy-spec-tabs__btn');
        if (!btn) return;
        const targetId = btn.getAttribute('aria-controls');
        btns.forEach(b => {
          const isActive = b === btn;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-selected', String(isActive));
        });
        panels.forEach(p => {
          const isActive = p.id === targetId;
          p.classList.toggle('is-active', isActive);
          p.hidden = !isActive;
        });
      });
    });
  }

  /* ── Countdown Timer ─────────────────────────────────── */
  function initCountdown() {
    const el = qs('[data-countdown]');
    if (!el) return;
    const endTime = Date.now() + parseInt(el.dataset.countdown || '86400') * 1000;

    const hEl = qs('[data-cd-hours]', el);
    const mEl = qs('[data-cd-minutes]', el);
    const sEl = qs('[data-cd-seconds]', el);

    function update() {
      const diff = Math.max(0, endTime - Date.now());
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (hEl) hEl.textContent = String(h).padStart(2, '0');
      if (mEl) mEl.textContent = String(m).padStart(2, '0');
      if (sEl) sEl.textContent = String(s).padStart(2, '0');
      if (diff > 0) setTimeout(update, 1000);
    }
    update();
  }

  /* ── Toast ────────────────────────────────────────────── */
  function showToast(msg, duration) {
    const toast = qs('#HyToast');
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, duration || 3000);
  }

  /* ── Cart Count Sync (Dawn events & custom) ───────────── */
  function initCartSync() {
    document.addEventListener('cart:refresh', refreshCartCount);
    document.addEventListener('cart:updated', refreshCartCount);
    document.addEventListener('change', (e) => {
      if (e.target && e.target.closest('cart-items, .cart-item')) {
        setTimeout(refreshCartCount, 300);
      }
    });

    // Refresh on bfcache back/forward navigation and window focus
    window.addEventListener('pageshow', refreshCartCount);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        refreshCartCount();
      }
    });
    window.addEventListener('focus', refreshCartCount);

    // Initial load
    refreshCartCount();
  }

  /* ── Model Viewer removed (not in use) ──────────────── */

  /* ── Announcement Bar Dismiss ────────────────────────── */
  function initAnnouncementDismiss() {
    const bar = qs('.hy-announcement-bar');
    const btn = qs('.hy-announcement-bar__close');
    if (!bar || !btn) return;
    const key = 'hy-announcement-dismissed';
    if (sessionStorage.getItem(key)) {
      bar.hidden = true;
      return;
    }
    on(btn, 'click', () => {
      bar.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      bar.style.height = bar.offsetHeight + 'px';
      requestAnimationFrame(() => {
        bar.style.height = '0';
        bar.style.opacity = '0';
        bar.style.overflow = 'hidden';
        setTimeout(() => { bar.hidden = true; }, 320);
      });
      sessionStorage.setItem(key, '1');
    });
  }

  /* ── Native Review Pagination ──────────────────────────── */
  function initReviewPagination() {
    qsa('[data-review-grid]').forEach(grid => {
      const limit = parseInt(grid.dataset.limit) || 6;
      const items = qsa('[data-review-item]', grid);
      const paginationWrap = grid.nextElementSibling?.matches('[data-review-pagination]') ? grid.nextElementSibling : null;

      if (!items.length || !paginationWrap) return;

      const totalPages = Math.ceil(items.length / limit);
      if (totalPages <= 1) {
        paginationWrap.style.display = 'none';
        items.forEach(item => item.classList.add('is-visible'));
        return;
      }

      // Build pagination UI
      paginationWrap.innerHTML = '';
      paginationWrap.classList.add('is-active');
      const pageContainer = document.createElement('div');
      pageContainer.className = 'hy-pagination-numbers';
      paginationWrap.appendChild(pageContainer);

      let currentPage = 1;

      function renderPage(page) {
        currentPage = page;
        items.forEach((item, index) => {
          const isVisible = index >= (page - 1) * limit && index < page * limit;
          item.classList.toggle('is-visible', isVisible);
          item.style.display = isVisible ? 'flex' : 'none';
        });
        initReviewDates();

        // Update buttons
        pageContainer.innerHTML = '';
        for (let i = 1; i <= totalPages; i++) {
          const btn = document.createElement('button');
          btn.className = `hy-pagination-btn ${i === page ? 'is-active' : ''}`;
          btn.textContent = i;
          btn.onclick = () => {
            renderPage(i);
            // smooth scroll to top of grid with an offset for sticky header
            const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--hy-header-total')) || 80;
            const elementPosition = grid.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset - 24;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          };
          pageContainer.appendChild(btn);
        }
      }

      renderPage(1);
    });
  }

  /* ── Review Media Lightbox ────────────────────────────── */
  function initReviewLightbox() {
    let lightbox = qs('#HyReviewLightbox');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.id = 'HyReviewLightbox';
      lightbox.className = 'hy-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Photo preview');
      lightbox.innerHTML = `
        <div class="hy-lightbox__backdrop" data-lightbox-close></div>
        <div class="hy-lightbox__content">
          <button class="hy-lightbox__close" data-lightbox-close aria-label="Close full view">&times;</button>
          <img class="hy-lightbox__img" src="" alt="Customer review photo" style="display:none;">
          <div class="hy-lightbox__caption"></div>
        </div>
      `;
      document.body.appendChild(lightbox);
    }

    const imgEl = qs('.hy-lightbox__img', lightbox);
    const captionEl = qs('.hy-lightbox__caption', lightbox);

    function closeLightbox() {
      lightbox.classList.remove('is-active');
      document.body.style.overflow = '';
      if (imgEl) imgEl.src = '';
    }

    function openLightbox(src, author, location) {
      if (!src) return;
      imgEl.src = src;
      imgEl.style.display = 'block';
      let captionText = 'Verified Customer Review';
      if (author) {
        captionText = author + (location ? ` · ${location}` : '');
      }
      captionEl.textContent = captionText;
      lightbox.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    // Delegate click handler for any lightbox trigger
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox-trigger]');
      if (trigger) {
        e.preventDefault();
        const src = trigger.dataset.mediaSrc || trigger.querySelector('img')?.src;
        const author = trigger.dataset.author || '';
        const location = trigger.dataset.location || '';
        openLightbox(src, author, location);
        return;
      }

      if (e.target.closest('[data-lightbox-close]')) {
        e.preventDefault();
        closeLightbox();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox.classList.contains('is-active')) {
        closeLightbox();
      }
    });
  }

  /* ── Description Expand / Collapse ───────────────────── */
  function initDescExpand() {
    const wraps = qsa('[data-desc-expandable]');
    wraps.forEach(wrap => {
      const content = qs('[data-desc-content]', wrap);
      const toggle = qs('[data-desc-toggle]', wrap);
      const textSpan = qs('.hy-buy__desc-toggle-text', toggle);
      if (!content || !toggle) return;

      const checkOverflow = () => {
        if (wrap.classList.contains('is-expanded')) {
          toggle.style.display = 'inline-flex';
          return;
        }
        // If scroll height is noticeably taller than client height
        const isOverflowing = content.scrollHeight > content.clientHeight + 4;
        toggle.style.display = isOverflowing ? 'inline-flex' : 'none';
      };

      checkOverflow();
      window.addEventListener('resize', checkOverflow, { passive: true });

      on(toggle, 'click', () => {
        const isExp = wrap.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', String(isExp));
        if (textSpan) {
          textSpan.textContent = isExp ? 'Read less' : 'Read more';
        }
      });
    });
  }

  /* ── Dynamic Review Timestamps ────────────────────────── */
  function initReviewDates() {
    const dateEls = qsa('[data-review-date]');
    if (!dateEls.length) return;
    const now = new Date();

    function formatRelativeDate(dateInput) {
      if (!dateInput) return '';
      const trimmed = dateInput.trim();
      let date;

      // Check if it's an ISO date or parseable date (e.g. "2026-09-18", "2026-09-14", "18 Sep 2026")
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || !isNaN(Date.parse(trimmed))) {
        date = new Date(trimmed);
      } else {
        // Match relative day patterns if specified as offset
        const daysMatch = trimmed.match(/^(\d+)\s*(d|day|days)\s*ago$/i);
        const weeksMatch = trimmed.match(/^(\d+)\s*(w|wk|week|weeks)\s*ago$/i);
        const monthsMatch = trimmed.match(/^(\d+)\s*(m|mo|month|months)\s*ago$/i);

        if (daysMatch) {
          date = new Date(now.getTime() - parseInt(daysMatch[1], 10) * 86400000);
        } else if (weeksMatch) {
          date = new Date(now.getTime() - parseInt(weeksMatch[1], 10) * 7 * 86400000);
        } else if (monthsMatch) {
          date = new Date(now.getTime() - parseInt(monthsMatch[1], 10) * 30 * 86400000);
        } else {
          return trimmed;
        }
      }

      if (isNaN(date.getTime())) return trimmed;

      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffDays < 0 || diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 14) {
        return '1 week ago';
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} weeks ago`;
      } else if (diffDays < 60) {
        return '1 month ago';
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} months ago`;
      } else {
        const years = Math.floor(diffDays / 365);
        return years === 1 ? '1 year ago' : `${years} years ago`;
      }
    }

    dateEls.forEach(el => {
      const raw = el.getAttribute('data-review-date') || el.textContent;
      const formatted = formatRelativeDate(raw);
      if (formatted) el.textContent = formatted;
    });
  }

  /* ── Auto-Sync Rating from Testimonials to Buy Box ─────── */
  function initRatingSync() {
    const scoreEl = qs('.hy-rating-summary__num');
    const countWrap = qs('.hy-rating-summary__count');
    const buyScoreEl = qs('.hy-buy__rating-num');
    const buyCountEl = qs('.hy-buy__rating-count');

    if (scoreEl && buyScoreEl) {
      const liveScore = scoreEl.textContent.trim();
      if (liveScore) {
        buyScoreEl.textContent = liveScore;
      }
    }

    if (countWrap && buyCountEl) {
      const countMatch = countWrap.textContent.match(/(\d+)/);
      if (countMatch && countMatch[1]) {
        buyCountEl.textContent = `${countMatch[1]} Verified Reviews`;
      }
    }
  }

  /* ── Expose showToast globally for Liquid use ─────────── */
  window.HyShowToast = showToast;
  window.HyRefreshCart = refreshCartCount;
  window.HyInitReviewDates = initReviewDates;
  window.HyInitRatingSync = initRatingSync;

  /* ── Init All ─────────────────────────────────────────── */
  function init() {
    initStickyHeader();
    initMobileNav();
    initScrollReveal();
    initCountUp();
    initGallery();
    initVariantSelector();
    initQtySelector();
    initAjaxCart();
    initStickyATCBar();
    initQuickBuyDrawer();
    initAccordion();
    initTabs();
    initCountdown();
    initCartSync();
    // initModelViewer() — removed, model-viewer not used
    initAnnouncementDismiss();
    initReviewDates();
    initReviewPagination();
    initReviewLightbox();
    initRatingSync();
    initDescExpand();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
